import {
  TvPosterContact,
  TvPosterDate,
  TvPosterDesign,
  TvPosterGoal,
  TvPosterLayout,
  TvPosterProgramRow,
  TvPosterQr,
  TvPosterSpeaker,
  TvPosterVipps,
} from "types/TvPosterTypes";

/** Blokkene som kan slås av og på i editoren. */
export type BlockKey =
  | "date"
  | "place"
  | "speaker"
  | "program"
  | "qr"
  | "imageUrl"
  | "vipps"
  | "goal"
  | "signup"
  | "contact";

export const BLOCK_KEYS: BlockKey[] = [
  "date",
  "place",
  "speaker",
  "program",
  "qr",
  "imageUrl",
  "vipps",
  "goal",
  "signup",
  "contact",
];

export const BLOCK_LABELS: Record<BlockKey, string> = {
  date: "Dato og tid",
  place: "Sted",
  speaker: "Foredragsholder",
  program: "Program",
  qr: "QR-kode",
  imageUrl: "Bilde",
  vipps: "Vipps og kontonummer",
  goal: "Innsamlingsmål",
  signup: "Påmelding og pris",
  contact: "Kontaktperson",
};

/** Hvilke blokker hvert oppsett tegner. Resten ignoreres av backend. */
export const LAYOUT_BLOCKS: Record<TvPosterLayout, BlockKey[]> = {
  ANNOUNCEMENT: [
    "date",
    "place",
    "speaker",
    "qr",
    "imageUrl",
    "signup",
    "contact",
  ],
  EVENT: ["date", "place", "speaker", "qr", "signup", "contact"],
  PROGRAM: ["program", "date", "place", "qr", "signup", "contact"],
  FUNDRAISER: ["goal", "vipps", "qr", "contact"],
  IMAGE: [],
};

export const MAX = {
  prompt: 300,
  eyebrow: 28,
  title: 60,
  subtitle: 70,
  body: 240,
  repeat: 40,
};

/** Verdiene i hver blokk. Strenger er "" i stedet for null i skjemaet. */
export interface BlockValues {
  date: {
    date: string;
    from: string;
    to: string;
    note: string;
    repeat: string;
  };
  place: string;
  speaker: { name: string; role: string; photoUrl: string };
  program: TvPosterProgramRow[];
  qr: { url: string; imageUrl: string; label: string };
  imageUrl: string;
  vipps: { number: string; account: string };
  goal: { project: string; target: string; raised: string };
  signup: string;
  contact: { name: string; phone: string };
}

export interface EditorState {
  layout: TvPosterLayout;
  theme: TvPosterDesign["theme"];
  eyebrow: string;
  title: string;
  subtitle: string;
  body: string;
  arabic: string;
  ownImageUrl: string;
  on: Record<BlockKey, boolean>;
  /** Verdiene huskes også når blokken er slått av. */
  values: BlockValues;
  qrMode: "link" | "image";
}

const s = (v: string | null | undefined) => v ?? "";
const n = (v: number | null | undefined) =>
  v === null || v === undefined ? "" : String(v);

export const emptyValues = (): BlockValues => ({
  date: { date: "", from: "", to: "", note: "", repeat: "" },
  place: "",
  speaker: { name: "", role: "", photoUrl: "" },
  program: [{ time: "", text: "" }],
  qr: { url: "", imageUrl: "", label: "" },
  imageUrl: "",
  vipps: { number: "", account: "" },
  goal: { project: "", target: "", raised: "" },
  signup: "",
  contact: { name: "", phone: "" },
});

const allOff = (): Record<BlockKey, boolean> =>
  BLOCK_KEYS.reduce(
    (acc, k) => ({ ...acc, [k]: false }),
    {} as Record<BlockKey, boolean>,
  );

export const emptyEditor = (): EditorState => ({
  layout: "ANNOUNCEMENT",
  theme: "NIGHT",
  eyebrow: "",
  title: "",
  subtitle: "",
  body: "",
  arabic: "",
  ownImageUrl: "",
  on: allOff(),
  values: emptyValues(),
  qrMode: "link",
});

/**
 * Gjør et design om til skjemaverdier. Blokker som er av i designet beholder
 * verdiene fra `previous` (så de ikke forsvinner når et AI-forslag tas i bruk).
 */
export const fromDesign = (
  design: TvPosterDesign,
  previous: EditorState = emptyEditor(),
): EditorState => {
  const prev = previous.values;
  const values: BlockValues = {
    date: design.date
      ? {
          date: s(design.date.date),
          from: s(design.date.from),
          to: s(design.date.to),
          note: s(design.date.note),
          repeat: s(design.date.repeat),
        }
      : prev.date,
    place: design.place ?? prev.place,
    speaker: design.speaker
      ? {
          name: s(design.speaker.name),
          role: s(design.speaker.role),
          photoUrl: s(design.speaker.photoUrl),
        }
      : prev.speaker,
    program:
      design.program && design.program.length > 0
        ? design.program.map((r) => ({ time: s(r.time), text: s(r.text) }))
        : prev.program,
    qr: design.qr
      ? {
          url: s(design.qr.url),
          imageUrl: s(design.qr.imageUrl),
          label: s(design.qr.label),
        }
      : prev.qr,
    imageUrl: design.imageUrl ?? prev.imageUrl,
    vipps: design.vipps
      ? { number: s(design.vipps.number), account: s(design.vipps.account) }
      : prev.vipps,
    goal: design.goal
      ? {
          project: s(design.goal.project),
          target: n(design.goal.target),
          raised: n(design.goal.raised),
        }
      : prev.goal,
    signup: design.signup ?? prev.signup,
    contact: design.contact
      ? { name: s(design.contact.name), phone: s(design.contact.phone) }
      : prev.contact,
  };
  const on = BLOCK_KEYS.reduce(
    (acc, k) => ({
      ...acc,
      [k]:
        k === "program"
          ? !!design.program && design.program.length > 0
          : design[k] !== null && design[k] !== undefined,
    }),
    {} as Record<BlockKey, boolean>,
  );
  return {
    layout: design.layout ?? "ANNOUNCEMENT",
    theme: design.theme ?? "NIGHT",
    eyebrow: s(design.eyebrow),
    title: s(design.title),
    subtitle: s(design.subtitle),
    body: s(design.body),
    arabic: s(design.arabic),
    ownImageUrl: design.ownImageUrl ?? previous.ownImageUrl,
    on,
    values,
    qrMode:
      design.qr && !design.qr.url && design.qr.imageUrl
        ? "image"
        : design.qr
          ? "link"
          : previous.qrMode,
  };
};

/** Tom/blank tekst blir null. */
const t = (v: string) => (v.trim() ? v.trim() : null);

const toNumber = (v: string): number | null => {
  const digits = v.replace(/\D/g, "");
  return digits ? parseInt(digits, 10) : null;
};

/** Skjemaverdier → design som sendes til backend. Av-blokker blir null. */
export const toDesign = (e: EditorState): TvPosterDesign => {
  const v = e.values;
  const block = <T>(k: BlockKey, value: () => T): T | null =>
    e.on[k] ? value() : null;
  return {
    layout: e.layout,
    theme: e.theme,
    eyebrow: t(e.eyebrow),
    title: e.title.trim(),
    subtitle: t(e.subtitle),
    body: t(e.body),
    arabic: t(e.arabic),
    date: block<TvPosterDate>("date", () => ({
      date: t(v.date.date),
      from: t(v.date.from),
      to: t(v.date.to),
      note: t(v.date.note),
      repeat: t(v.date.repeat),
    })),
    place: block("place", () => t(v.place)),
    speaker: block<TvPosterSpeaker>("speaker", () => ({
      name: t(v.speaker.name),
      role: t(v.speaker.role),
      photoUrl: t(v.speaker.photoUrl),
    })),
    program: block<TvPosterProgramRow[]>("program", () =>
      v.program
        .map((r) => ({ time: r.time.trim(), text: r.text.trim() }))
        .filter((r) => r.time || r.text),
    ),
    qr: block<TvPosterQr>("qr", () => ({
      url: e.qrMode === "link" ? t(v.qr.url) : null,
      imageUrl: e.qrMode === "image" ? t(v.qr.imageUrl) : null,
      label: t(v.qr.label),
    })),
    imageUrl: block("imageUrl", () => t(v.imageUrl)),
    vipps: block<TvPosterVipps>("vipps", () => ({
      number: t(v.vipps.number),
      account: t(v.vipps.account),
    })),
    goal: block<TvPosterGoal>("goal", () => ({
      project: t(v.goal.project),
      target: toNumber(v.goal.target),
      raised: toNumber(v.goal.raised),
    })),
    signup: block("signup", () => t(v.signup)),
    contact: block<TvPosterContact>("contact", () => ({
      name: t(v.contact.name),
      phone: t(v.contact.phone),
    })),
    ownImageUrl: t(e.ownImageUrl),
  };
};

/** Om det er nok innhold til å be backend om en forhåndsvisning. */
export const canPreview = (d: TvPosterDesign) =>
  d.layout === "IMAGE" ? !!d.ownImageUrl : !!d.title;

/** Feil som hindrer lagring, eller null. */
export const saveProblem = (d: TvPosterDesign): string | null => {
  if (!d.title) return "Skriv en tittel.";
  if (d.title.length > MAX.title)
    return `Tittelen kan ha maks ${MAX.title} tegn.`;
  if (d.body && d.body.length > MAX.body)
    return `Teksten kan ha maks ${MAX.body} tegn.`;
  if (d.layout === "IMAGE" && !d.ownImageUrl)
    return "Last opp plakaten som skal vises.";
  return null;
};
